import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { AlertTriangle, Phone, Mail, Shield, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';

const MedicareFraud = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="medicare-fraud-page overflow-x-hidden">
      <SEO 
        title="Medicare Fraud Prevention"
        description="Protect yourself from Medicare fraud and scam calls. Learn how to identify fraudulent Medicare calls, report scams, and safeguard your personal information. Free fraud prevention education."
        keywords="Medicare fraud, Medicare scams, Medicare fraud prevention, scam calls, Medicare identity theft, protect Medicare card, report Medicare fraud, Medicare security"
        path="/medicare-calls"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-red-50 to-orange-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <AlertTriangle className="text-red-600" size={64} />
              <h1 className="text-5xl font-bold text-gray-900">MEDICARE FRAUD</h1>
            </div>
            <p className="text-3xl font-bold text-red-600 mb-4">PROTECT YOURSELF</p>
            <p className="text-2xl text-gray-800 max-w-4xl mx-auto">
              Medicare fraud costs taxpayers billions of dollars each year and puts your personal information at risk.
            </p>
          </div>
        </div>
      </section>

      {/* Educational Video - Moved to Top */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">Learn More About Medicare Fraud</h2>
          <div className="bg-gray-100 rounded-lg shadow-2xl p-4">
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/ivDx78vhjCs?playsinline=1"
                title="Medicare Fraud Education Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-lg"
              ></iframe>
            </div>
          </div>
          <p className="text-center text-lg text-gray-600 mt-4 italic">
            Watch this important video to learn how to identify and report Medicare fraud
          </p>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-12 px-4 bg-yellow-50 border-y-4 border-yellow-400">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">⚠️ BEWARE OF THESE COMMON SCAMS ⚠️</h2>
          
          <div className="space-y-6">
            <Card className="border-4 border-red-300 bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-red-600 mb-4">Unsolicited Phone Calls</h3>
                <p className="text-xl text-gray-800 mb-3">Scammers call claiming to be from Medicare asking for:</p>
                <ul className="list-disc list-inside text-lg text-gray-800 space-y-2 ml-4">
                  <li>Your Medicare number</li>
                  <li>Bank account information</li>
                  <li>Social Security number</li>
                  <li>Credit card details</li>
                </ul>
                <p className="text-xl font-bold text-red-700 mt-4">⚠️ MEDICARE WILL NEVER CALL YOU UNSOLICITED!</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-red-300 bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-red-600 mb-4">"Free" Medical Equipment or Services</h3>
                <p className="text-xl text-gray-800">
                  Offers for free wheelchairs, braces, or other equipment in exchange for your Medicare number. 
                  These are often scams to bill Medicare for unnecessary items.
                </p>
              </CardContent>
            </Card>

            <Card className="border-4 border-red-300 bg-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-red-600 mb-4">Fake Medicare Cards</h3>
                <p className="text-xl text-gray-800">
                  Callers claim your Medicare card is expiring or needs to be replaced and request personal information or payment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How to Protect Yourself */}
      <section className="py-16 px-4 bg-gray-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">HOW TO PROTECT YOURSELF</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-4 border-green-400 bg-green-50">
              <CardContent className="p-8">
                <Shield className="w-16 h-16 text-green-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">DO:</h3>
                <ul className="space-y-3 text-lg text-gray-800">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-2xl">✓</span>
                    <span>Guard your Medicare and Social Security numbers like a credit card</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-2xl">✓</span>
                    <span>Review your Medicare Summary Notices for errors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-2xl">✓</span>
                    <span>Keep personal documents in a safe place</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-2xl">✓</span>
                    <span>Work only with trusted insurance agents</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-4 border-red-400 bg-red-50">
              <CardContent className="p-8">
                <AlertTriangle className="w-16 h-16 text-red-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">DON'T:</h3>
                <ul className="space-y-3 text-lg text-gray-800">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-2xl">✗</span>
                    <span>Give your Medicare number to strangers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-2xl">✗</span>
                    <span>Accept unsolicited offers for services or equipment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-2xl">✗</span>
                    <span>Let anyone borrow your Medicare card</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-2xl">✗</span>
                    <span>Provide personal information over the phone to unsolicited callers</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Medicare Fraud Education Seminar CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-5xl">
          {/* Seminar Image */}
          <div className="mb-8 text-center">
            <img 
              src="/images/fraud-1.png"
              alt="Medicare Fraud Protection Seminar with Paulette Abdullah"
              className="mx-auto rounded-lg shadow-2xl max-w-3xl w-full"
            />
          </div>

          <div className="bg-white rounded-lg shadow-2xl p-12 border-4 border-teal-400">
            <h2 className="text-4xl font-bold text-center mb-6 text-gray-900">
              FREE Medicare Fraud Protection Seminar
            </h2>
            <p className="text-2xl text-center text-gray-800 mb-8">
              Join Paulette Abdullah for an educational session on how to protect yourself from Medicare fraud and scams
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-teal-100 rounded-lg p-6 mb-3">
                  <p className="text-lg font-bold text-gray-900">Duration</p>
                  <p className="text-2xl font-bold text-teal-600">60 Minutes</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-teal-100 rounded-lg p-6 mb-3">
                  <p className="text-lg font-bold text-gray-900">Format</p>
                  <p className="text-2xl font-bold text-teal-600">Virtual or In-Person</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-teal-100 rounded-lg p-6 mb-3">
                  <p className="text-lg font-bold text-gray-900">Cost</p>
                  <p className="text-2xl font-bold text-teal-600">FREE</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button 
                onClick={() => setShowModal(true)}
                size="lg" 
                className="bg-teal-600 hover:bg-teal-700 text-white text-2xl px-16 py-8 mb-4"
              >
                Schedule a Seminar
              </Button>
              <p className="text-lg text-gray-700">
                Perfect for senior centers, community groups, and organizations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Report Fraud Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl font-bold mb-8 text-gray-900">HOW TO REPORT MEDICARE FRAUD</h2>
          
          <div className="bg-red-50 border-4 border-red-400 rounded-lg p-8 mb-8">
            <p className="text-2xl font-bold text-gray-900 mb-4">If you suspect Medicare fraud:</p>
            <div className="space-y-4 text-xl text-gray-800">
              <p className="flex items-center justify-center gap-3">
                <Phone size={28} className="text-red-600" />
                <span>Call: <strong className="text-2xl">1-800-MEDICARE (1-800-633-4227)</strong></span>
              </p>
              <p className="flex items-center justify-center gap-3">
                <ExternalLink size={28} className="text-red-600" />
                <span>Visit: <a href="https://tips.oig.hhs.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold">tips.oig.hhs.gov</a></span>
              </p>
            </div>
          </div>

          <p className="text-xl text-gray-800 mb-8">
            Or contact your trusted insurance agent for guidance:
          </p>
          
          <div className="space-y-4">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-2xl px-12 py-8">
              <a href="tel:734.209.6712">
                <Phone className="mr-2" />
                Call Paulette: 734.209.6712
              </a>
            </Button>
            <div className="flex items-center justify-center gap-2 text-xl text-gray-800">
              <Mail size={24} />
              <span>Email: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700 font-semibold">Paulette@llplans.com</a></span>
            </div>
          </div>
        </div>
      </section>

      {/* Seminar Booking Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Schedule a Medicare Fraud Protection Seminar</h3>
            <p className="text-xl text-gray-800 mb-6">
              Please contact us to schedule a seminar for your group or organization:
            </p>
            <div className="space-y-4 mb-8">
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-xl py-6">
                <a href="tel:734.209.6712">
                  <Phone className="mr-2" />
                  Call: 734.209.6712
                </a>
              </Button>
              <Button asChild variant="outline" className="w-full border-teal-600 text-teal-600 hover:bg-teal-50 text-xl py-6">
                <a href="mailto:Paulette@llplans.com?subject=Medicare%20Fraud%20Seminar%20Request">
                  <Mail className="mr-2" />
                  Email: Paulette@llplans.com
                </a>
              </Button>
            </div>
            <Button 
              onClick={() => setShowModal(false)}
              variant="outline"
              className="w-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicareFraud;
